import React, { memo } from 'react';

import { cn } from '@lyearn/style';
import * as Editor from '@/components/Editor/PlainEditor/components/Editor';
import Loader from '@/components/Loader';
import { GenerativeTaskContextProvider } from '@/components/LyearnAI/context/generativeTaskContext';
import useGenerativeTaskContextValue from '@/components/LyearnAI/hooks/useGenerativeTaskContextValue';

import { AIEditorContextProvider } from '../../context/AIEditorContext';
import useAIEditorContextValue from '../../hooks/useAIEditorContextValue';
import useAIEditorProps from '../../hooks/useAIEditorProps';
import { AIBaseEditorProps, AIEditorState } from '../../types';
import AIEditorFeedback from './components/AIEditorFeedback';
import AISuggestionContainer from './components/AISuggestionContainer';

const AIBaseEditor: React.FC<AIBaseEditorProps> = (props) => {
  const {
    className,
    classes,
    editorCoreProps,
    textUseCase,
    topic,
    onTopicError,
    onChangeText,
    disabled,
    SuggestionComponent,
    FeedbackComponent,
    ...editorProps
  } = props;

  const generativeTaskContextValue = useGenerativeTaskContextValue();
  const aiEditorContextValue = useAIEditorContextValue(props, generativeTaskContextValue);
  const { editorState, isAIActive, showEditor, suggestionBodyHighlighted } = aiEditorContextValue;
  const { editorOptionMap, editorOptions } = useAIEditorProps(props);

  return (
    <React.Suspense fallback={<Loader />}>
      <GenerativeTaskContextProvider {...generativeTaskContextValue}>
        <AIEditorContextProvider {...aiEditorContextValue}>
          <Editor.Main
            className={cn(
              'relative flex flex-col overflow-hidden',
              classes?.root,
              className,
              isAIActive ? classes?.rootActiveAI : '',
            )}
            disabled={disabled}
            onChangeText={onChangeText}
            {...editorProps}>
            <Editor.EditorCore
              className={cn('flex-1 overflow-y-auto overscroll-y-contain', classes?.editor)}
              disableSelection={isAIActive}
              showEditor={showEditor}
              {...editorCoreProps}>
              {SuggestionComponent ? (
                <SuggestionComponent />
              ) : (
                <AISuggestionContainer
                  bodyTypographyProps={{
                    color: suggestionBodyHighlighted ? 'text-text-white' : 'text-text-paragraph',
                  }}
                  className="min-h-[13.7rem] px-16 py-12"
                  classes={{
                    header: { root: 'mb-8' },
                    body: suggestionBodyHighlighted ? 'bg-bg-highlight' : '',
                  }}
                  showHeader={editorState === AIEditorState.DraftCompletion}
                  showHeaderIcon={false}
                />
              )}
              {FeedbackComponent ? <FeedbackComponent /> : <AIEditorFeedback />}
            </Editor.EditorCore>
            {editorProps.medias?.length ? <Editor.AttachmentContainer /> : null}
            <Editor.Toolbar
              classes={classes?.toolbar}
              editorOptionMap={editorOptionMap}
              editorOptions={editorOptions}
              isMedia={!!editorProps.onChangeMedias}
            />
          </Editor.Main>
        </AIEditorContextProvider>
      </GenerativeTaskContextProvider>
    </React.Suspense>
  );
};

export default memo(AIBaseEditor);
